import { ModelDropdownWithStation } from "@/components/molecules/ModelDropdown/ModelDropdownWithStation";
import { LayerKindName } from "@/layers/utility/createLayerObject";
import type { DraggableItemIdentifier } from "@/overlay/components/CommonParts/SortableList";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";
import type { AdvancedThreshold, ThresholdColor } from "@/overlay/components/CommonParts/Threshold/types";
import { PoiTable } from "@/overlay/components/PoiManagerWindow/PoITable";
import { WeatherParameterForm } from "@/overlay/components/WeatherParameterForm";
import { weatherParamOptionFilter } from "@/overlay/components/WeatherParameterForm/optionFilter";
import { WeatherParameterSearchForm } from "@/overlay/components/WeatherParameterSearchForm/WeatherParamPicker";
import { FormField } from "@/overlay/components/form-field";
import * as windowManager from "@/overlay/components/window";
import { DefaultThreshold } from "@/reducer/client-models";
import type { PoiDataGrid } from "@/threads/DataProcessingThread/types";
import { searchEnginesWeatherParams } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import type { CrossModelParameterSpecification } from "@mm/api-layers.meteomatics.com";
import { Input } from "@mm/metx-design-system";
import type { ExactMatchResult, HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import * as weatherParamUtil from "@mm/weather-parameter-utils";
import { useEffect, useMemo, useState } from "react";
import type { AlertLayerDraft } from "./alertLayerDraft";
import { isAddAlertLayerFormValid } from "./validation";

type AddAlertLayerFormProps = {
  mapId: number;
  poiCoords: PoiDataGrid;
  onPoiCoordsChange: (coords: PoiDataGrid) => void;
  onApplyDisabledChange: (disabled: boolean) => void;
  onDraftChange: (draft: AlertLayerDraft | null) => void;
};

export function AddAlertLayerForm({
  mapId,
  poiCoords,
  onPoiCoordsChange,
  onApplyDisabledChange,
  onDraftChange,
}: AddAlertLayerFormProps) {
  const [title, setTitle] = useState("");
  const [currentParam, setCurrentParam] = useState<PartialWeatherParameter | null>(null);
  const [currentModel, setCurrentModel] = useState<string | null>(null);
  const [thresholds, setThresholds] = useState<ThresholdColor[]>([]);
  const [advancedThresholds, setAdvancedThresholds] = useState<AdvancedThreshold[]>([]);

  const paramSearchOptions = useMemo(
    () => [
      {
        label: "Standard",
        searchEngine: searchEnginesWeatherParams.standard,
      },
    ],
    [],
  );

  const paramUnit = currentParam ? weatherParamUtil.parameterToString(currentParam)?.formatted ?? "" : "";

  useEffect(() => {
    if (!currentParam || thresholds.length > 0) {
      return;
    }
    setThresholds([DefaultThreshold]);
  }, [currentParam, thresholds.length]);

  const isValid = isAddAlertLayerFormValid({
    title,
    locations: poiCoords,
    currentParam,
    thresholds,
    advancedThresholds,
  });

  const draft = useMemo<AlertLayerDraft | null>(() => {
    if (!isValid) {
      return null;
    }

    return {
      title,
      parameterName: paramUnit,
      sourceModel: currentModel,
      locations: poiCoords
        .filter((row) => row.lat != null && row.lat !== "" && row.lon != null && row.lon !== "")
        .map(({ lat, lon, name, label }) => ({
          lat,
          lon,
          label: name ?? label,
        })),
      thresholds,
    };
  }, [currentModel, isValid, paramUnit, poiCoords, thresholds, title]);

  useEffect(() => {
    onApplyDisabledChange(!isValid);
    onDraftChange(draft);
  }, [draft, isValid, onApplyDisabledChange, onDraftChange]);

  function update(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    setCurrentParam(param);
  }

  function onSelectLayerGroup(result: CrossModelParameterSpecification) {
    const nextParam = weatherParamUtil.autofill({
      parameter: {
        ...result,
        layers: result.layers.map((layer) => weatherParamOptionFilter(layer, LayerKindName.GenericPoiLayerDescription)),
      },
    });
    setCurrentParam(nextParam);
  }

  function onSelectExactParam(result: ExactMatchResult) {
    const startParameter = {
      parameter: result.layerGroup,
      narrowed_selection: {
        layerIdx: result.matchingLayers[0].layerIdx,
        // biome-ignore lint/style/noNonNullAssertion: TODO groups
        fields: result.matchingLayers[0].match.groups!,
      },
    };
    setCurrentParam(weatherParamUtil.autofill(startParameter));
  }

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    setThresholds((currentThresholds) => {
      const copyOfThresholds = [...currentThresholds];
      const fromIdx = dragStart.index;
      let toIdx = dropTarget.index;
      if (toIdx >= copyOfThresholds.length) {
        toIdx = copyOfThresholds.length - 1;
      }
      if (toIdx < 0) {
        toIdx = 0;
      }
      const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
      copyOfThresholds.splice(toIdx, 0, targetThreshold);
      return copyOfThresholds;
    });
  }

  return (
    <>
      <windowManager.WindowSection>
        <Input
          label={t`Alert Title`}
          fullWidth
          placeholder={t`Enter alert title`}
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
        />
      </windowManager.WindowSection>
      <windowManager.WindowSection>
        <PoiTable initialData={poiCoords} mapId={mapId} onChange={onPoiCoordsChange} />
      </windowManager.WindowSection>
      <windowManager.WindowSection>
        <WeatherParameterSearchForm
          searchEngineOptions={paramSearchOptions}
          onSelectLayerGroup={onSelectLayerGroup}
          onSelectExactParam={onSelectExactParam}
          onChangeSearchEngine={() => setCurrentParam(null)}
        />
        {currentParam && (
          <>
            <WeatherParameterForm
              layerKind={LayerKindName.GenericPoiLayerDescription}
              withTitle={true}
              showExactParameter={true}
              autofill={false}
              parameter={currentParam}
              onChange={update}
            />
            <FormField label={t`Source`}>
              <ModelDropdownWithStation
                onModelChange={setCurrentModel}
                availableModels={currentParam.parameter.available_models}
                currentModel={currentModel}
              />
            </FormField>
            <CompleteThreshold
              onMoveIndex={moveThresholdIdx}
              updateThresholds={setThresholds}
              updateAdvancedThresholds={setAdvancedThresholds}
              thresholds={thresholds}
              advancedThresholds={advancedThresholds}
              paramUnit={paramUnit}
              enableAdvancedThreshold={false}
              isPoi={true}
            />
          </>
        )}
      </windowManager.WindowSection>
    </>
  );
}
